import React, { useEffect, useRef } from 'react';
import { ReactSVG } from 'react-svg';
import { Icons } from '@/assets';
import LoadingV2 from '@/components/Loading';
import liveUnloadPlannerTestData from './data/liveUnloadPlannerTestData';
import { LiveUnloadPlannerCard } from '@/components/AutobookPlanner';
import { useLiveUnloadPlannerApi } from './useLiveUnloadPlannerApi';
import styles from './plannerPage.module.css';

const MOCK_API_MS = 450;

export function fetchLiveUnloadPlannerPageMock(
  offset,
  limit,
  pool = liveUnloadPlannerTestData
) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const items = pool.slice(offset, offset + limit);
      resolve({ items, total: pool.length });
    }, MOCK_API_MS);
  });
}

export function LiveUnloadPlannerTab({ distanceLimitMi, containerSearch }) {
  const {
    items,
    initialLoading,
    loadingMore,
    hasMore,
    error,
    loadNextPageRef,
    filteredTotal,
  } = useLiveUnloadPlannerApi(distanceLimitMi, containerSearch);

  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    if (!hasMore) return undefined;

    const maybeLoadNext = () => {
      if (loadingMore || !hasMore) return;
      const load = loadNextPageRef.current;
      if (typeof load === 'function') load();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        maybeLoadNext();
      },
      { root: null, rootMargin: '160px 0px', threshold: 0 }
    );
    observer.observe(el);

    /**
     * Fallback: when observer misses a transition (common when sentinel stays in view),
     * load next page on near-bottom window scroll.
     */
    const onScroll = () => {
      if (loadingMore || !hasMore) return;
      const viewportBottom = window.scrollY + window.innerHeight;
      const triggerAt = document.documentElement.scrollHeight - 220;
      if (viewportBottom >= triggerAt) {
        maybeLoadNext();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, loadingMore, loadNextPageRef]);

  const showEmpty =
    !initialLoading && !error && filteredTotal === 0 && !loadingMore;

  return (
    <div className={styles.liveUnloadSection}>
      <div className={styles.liveUnloadHeader}>
        <ReactSVG
          src={Icons.clock}
          className={styles.liveUnloadClock}
          beforeInjection={(svg) => {
            svg.setAttribute('width', '24');
            svg.setAttribute('height', '24');
            svg.querySelectorAll('path').forEach((path) => {
              path.setAttribute('fill', '#131517');
            });
          }}
        />
        <div>
          <h2 className={styles.liveUnloadTitle}>Live Unload Planner</h2>
          <p className={styles.liveUnloadSubtitle}>
            Timeline keeps unloading time for live unload containers.
          </p>
        </div>
      </div>

      {initialLoading ? (
        <div className={styles.liveUnloadList} aria-busy="true">
          <LoadingV2 />
        </div>
      ) : null}

      {error ? (
        <p className={styles.liveUnloadEnd} role="alert">
          {error}
        </p>
      ) : null}

      {!initialLoading && !error ? (
        <div
          className={styles.liveUnloadList}
          role="feed"
          aria-busy={loadingMore}
          aria-label="Live unload timeline cards">
          {showEmpty ? (
            <p className={styles.liveUnloadEnd}>
              No containers match your filters.
            </p>
          ) : (
            <>
              {items.map((card) => (
                <LiveUnloadPlannerCard key={card.id} card={card} />
              ))}
              {loadingMore ? (
                <p className={styles.liveUnloadLoading}>Loading…</p>
              ) : null}
              {/* {!hasMore && items.length > 0 ? (
                <p className={styles.liveUnloadEnd}>End of list</p>
              ) : null} */}
              <div
                ref={sentinelRef}
                className={styles.liveUnloadSentinel}
                aria-hidden
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
